import { useEffect, useMemo, useState } from "react";
import type { AppEvent } from "../calendar/types";
import { normalizeCourseKey } from "../../shared/utils/normalizeCourse";
import { useSettings } from "./useSettings";
import { useT } from "../i18n/useT";

type CourseRow = { key: string; name: string; teacher?: string; room?: string };

function buildHiddenKey(name: string, teacher?: string) {
  const base = normalizeCourseKey(name);
  if (!base) return "";
  const t = normalizeCourseKey(teacher || "");
  return t ? `${base}::${t}` : base;
}

function collectCourses(events: AppEvent[]) {
  const map = new Map<string, { name: string; teacher?: string; room?: string }>(); // key -> data
  for (const e of events) {
    const name = (e.courseName || e.title || "").trim();
    if (!name) continue;
    const key = buildHiddenKey(name, e.teacher || undefined);
    if (!key) continue;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, { name, teacher: e.teacher || undefined, room: e.room || undefined });
    } else {
      if (!existing.teacher && e.teacher) existing.teacher = e.teacher;
      if (!existing.room && e.room) existing.room = e.room;
    }
  }
  return Array.from(map.entries())
    .map(([key, data]) => ({ key, name: data.name, teacher: data.teacher, room: data.room }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function isCancelledEvent(e: AppEvent) {
  const text = `${e.title || ""} ${e.description || ""}`.toLowerCase();
  return text.includes("отмен") || text.includes("cancel");
}

export function HiddenSubjectsSheet({
  open,
  onClose,
  events,
  inline,
}: {
  open: boolean;
  onClose: () => void;
  events: AppEvent[];
  inline?: boolean;
}) {
  const t = useT();
  const [s, setS] = useSettings();
  const [q, setQ] = useState("");
  const [tab, setTab] = useState<"shown" | "hidden">("shown");

  const all = useMemo<CourseRow[]>(() => collectCourses(events), [events]);
  const hidden = useMemo(() => new Set(s.hiddenCourses || []), [s.hiddenCourses]);
  const cancelled = useMemo(() => {
    const set = new Set<string>();
    for (const e of events) {
      if (!isCancelledEvent(e)) continue;
      const name = (e.courseName || e.title || "").trim();
      if (!name) continue;
      const key = buildHiddenKey(name, e.teacher || undefined);
      if (key) set.add(key);
    }
    return set;
  }, [events]);
  const hasHidden = hidden.size > 0;

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const allWithHidden = useMemo(() => {
    const map = new Map(all.map((x) => [x.key, x] as const));
    for (const key of hidden) {
      if (!map.has(key)) {
        const baseName = key.split("::")[0] || key;
        map.set(key, { key, name: baseName });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [all, hidden]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const base = tab === "hidden"
      ? allWithHidden.filter((x) => hidden.has(x.key) || cancelled.has(x.key))
      : allWithHidden.filter((x) => !hidden.has(x.key) && !cancelled.has(x.key));
    if (!qq) return base;
    return base.filter((x) => x.name.toLowerCase().includes(qq));
  }, [allWithHidden, q, tab, hidden, cancelled]);

  const toggle = (key: string) => {
    const cur = new Set(s.hiddenCourses || []);
    if (cur.has(key)) cur.delete(key);
    else cur.add(key);
    setS({ hiddenCourses: Array.from(cur) });
  };

  if (!open) return null;

  const content = (
    <>
      <div className="sheet-head">
        <div>
          <div className="sheet-title">{t("settings.subjects")}</div>
          <div className="muted small">{t("settings.manageHidden")}</div>
        </div>
        <button className="btn ghost" onClick={onClose} aria-label={t("common.close")}>
          x
        </button>
      </div>

      <div className="sheet-body">
        <div className="tabs" role="tablist" aria-label={t("settings.subjects")}>
          <button
            className={`tab ${tab === "shown" ? "is-active" : ""}`}
            onClick={() => setTab("shown")}
            role="tab"
            aria-selected={tab === "shown"}
          >
            {t("settings.subjects.shown")} <span className="muted">({all.length - hidden.size})</span>
          </button>
          <button
            className={`tab ${tab === "hidden" ? "is-active" : ""}`}
            onClick={() => setTab("hidden")}
            role="tab"
            aria-selected={tab === "hidden"}
          >
            {t("settings.subjects.hidden")} <span className="muted">({hidden.size})</span>
          </button>
        </div>

        <div className="sheet-tools">
          <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("settings.subjects.search")} />
          <button
            className={`btn ${s.hideOnlineLessons ? "primary" : "ghost"} sheet-action-btn`}
            onClick={() => setS({ hideOnlineLessons: !s.hideOnlineLessons })}
          >
            {s.hideOnlineLessons ? t("settings.subjects.showOnlineLessons") : t("settings.subjects.hideOnlineLessons")}
          </button>
          <button
            className="btn ghost sheet-action-btn"
            onClick={() => setS({ hiddenCourses: [] })}
            disabled={!hasHidden || tab !== "hidden"}
          >
            {t("settings.subjects.restoreAll")}
          </button>
        </div>

        <div className="sheet-list">
          {filtered.map((x) => {
            const isH = hidden.has(x.key);
            const isCancelled = cancelled.has(x.key);
            const actionLabel = tab === "shown"
              ? t("settings.subjects.shown")
              : t("settings.subjects.restore");
            return (
              <div key={x.key} className={`sheet-item ${isH ? "is-hidden" : ""}`}>
                <div className="left">
                  <div className="name">{x.name}</div>
                  {(x.teacher || x.room) ? (
                    <div className="sheet-details">
                      {x.teacher ? <span className="sheet-pill">{x.teacher}</span> : null}
                      {x.room ? <span className="sheet-pill is-room">{x.room}</span> : null}
                    </div>
                  ) : null}
                  <div className="sheet-meta muted small">
                    <span>{x.key}</span>
                    {isCancelled ? (
                      <span className="sheet-status is-hidden">{t("settings.subjects.cancelled")}</span>
                    ) : (
                    <span className={`sheet-status ${isH ? "is-hidden" : "is-shown"}`}>
                      {isH ? t("settings.subjects.hidden") : t("settings.subjects.shown")}
                    </span>
                    )}
                  </div>
                </div>
                <div className="right">
                  <button
                    className={`btn ghost sheet-toggle ${isH ? "is-show" : "is-hide"}`}
                    onClick={() => {
                      if (tab === "shown") {
                        if (!isH) toggle(x.key);
                        return;
                      }
                      if (isH) toggle(x.key);
                    }}
                  >
                    {actionLabel}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );

  if (inline) {
    return (
      <div className="sheet inline">
        {content}
      </div>
    );
  }

  return (
    <div className="sheet-backdrop is-modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="sheet is-modal" onClick={(e) => e.stopPropagation()}>
        {content}
      </div>
    </div>
  );
}
